import { MenuItem } from "@chakra-ui/react";
import { useInvestmentConfirmDelete } from "features/investments/InvestmentDelete";
import { InvestmentSetupTabProps } from "features/investments/setup/InvestmentSetupTabProps";
import {
  useInvestmentRetrieve,
  useInvestmentRetrieveSuspense,
} from "gen/endpoints/investment/investment";
import { InvestmentOrPortfolioEnum } from "gen/model/investmentOrPortfolioEnum";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Restricted } from "shared/components/access/Restricted";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { PageMenu } from "shared/components/layout/PageMenu";
import { PageTabs } from "shared/components/layout/PageTabs";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";

export const InvestmentSetupPage: React.FC = () => {
  const { t } = useTranslation(["investment", "common"]);
  const { fundFamilyId, investmentId } = useRequiredStringParams();
  const navigate = useNavigate();

  const { data: investmentData } = useInvestmentRetrieveSuspense(
    fundFamilyId,
    investmentId,
  );

  const { data: portfolio } = useInvestmentRetrieve(
    fundFamilyId,
    investmentData.portfolio_id!,
    {
      query: {
        enabled: !!investmentData.portfolio_id,
      },
    },
  );

  const investmentsPath = rootRoute({})
    .fundFamilies({})
    .fundFamily({ fundFamilyId })
    .investments({}).$;

  const tabProps: InvestmentSetupTabProps = {
    fundFamilyId,
    investmentId,
  };

  const hiddenTabs: Record<string, boolean> = {
    [t("investment:tabs.portfolioInvestments.title")]:
      investmentData.metadata.investment_or_portfolio ===
      InvestmentOrPortfolioEnum.portfolio,
  };

  const confirmDelete = useInvestmentConfirmDelete({
    fundFamilyId,
    isPortfolio: false,
    onSuccess: () => {
      navigate(investmentsPath);
    },
  });

  return (
    <PageContent contentWidth="boxed">
      <PageHeader
        title={investmentData.legal_name}
        backButton={{
          title: t("common:investments"),
          path: investmentsPath,
        }}
        actions={
          <Restricted permission={PermissionTypeEnum.config}>
            <PageMenu>
              <MenuItem
                className="destructive"
                onClick={() => confirmDelete({ investmentId })}
              >
                {t("common:delete")}
              </MenuItem>
            </PageMenu>
          </Restricted>
        }
        metadata={
          portfolio
            ? [
                {
                  title: t("common:portfolio"),
                  flattenIfSingle: true,
                  children: [
                    {
                      title: portfolio.legal_name,
                      to: rootRoute({})
                        .fundFamilies({})
                        .fundFamily({ fundFamilyId })
                        .portfolios({})
                        .portfolio({ portfolioId: portfolio.id! }).$,
                    },
                  ],
                },
                {
                  title: t("common:investment"),
                },
              ]
            : undefined
        }
      />
      <PageBody>
        <PageTabs context={tabProps} hiddenTabs={hiddenTabs} />
      </PageBody>
    </PageContent>
  );
};
